import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiShield, FiClock, FiDollarSign, FiTool, FiAward, FiUsers, FiCheck, FiHeart, FiTarget, FiStar } from 'react-icons/fi';
import colors from '../styles/colors';
import usePageMeta from '../hooks/usePageMeta';

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// HERO SECTION
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(10, 10, 10, 0.85) 0%, rgba(26, 26, 26, 0.8) 100%),
    url('/images/bmw-front.jpg') center/cover no-repeat;
  padding: 120px 20px 80px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(135deg, transparent 0%, ${colors.accent.main}10 100%);
    clip-path: polygon(30% 0, 100% 0, 100% 100%, 0% 100%);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const HeroContent = styled.div`
  max-width: 700px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${colors.accent.main}15;
  border: 1px solid ${colors.accent.main}30;
  border-radius: 50px;
  color: ${colors.accent.light};
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: clamp(3rem, 8vw, 5rem);
  color: ${colors.text.heading};
  margin-bottom: 24px;
  line-height: 1;

  span {
    color: ${colors.accent.main};
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${colors.text.light};
  line-height: 1.7;
  margin-bottom: 40px;
  max-width: 550px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 60px;
`;

const PrimaryBtn = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 36px;
  background: ${colors.accent.gradient};
  color: ${colors.text.white};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  border: 1px solid ${colors.accent.light};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 20px rgba(0, 102, 177, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 8px 30px rgba(0, 102, 177, 0.5);
  }

  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(5px);
  }
`;

const SecondaryBtn = styled(Link)`
  padding: 18px 36px;
  background: ${colors.metallic.darkSteel};
  color: ${colors.text.silver};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border: 1px solid ${colors.borderLight};
  border-radius: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 2px 10px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary.main};
    color: ${colors.text.white};
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 4px 15px rgba(0, 0, 0, 0.4);
  }
`;

const Features = styled.div`
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${colors.text.light};
  font-weight: 500;

  svg {
    color: ${colors.accent.main};
    font-size: 1.4rem;
  }
`;

// SPOTLIGHT SECTION - BMW & ACADEMY
const SpotlightSection = styled.section`
  padding: 100px 20px;
  background: ${colors.background.dark};
  position: relative;
`;

const SpotlightGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SpotlightCard = styled(Link)`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 40px;
  transition: all 0.4s ease;
  border: 1px solid ${colors.borderLight};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => props.$gradient};
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%);
    z-index: 1;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: ${props => props.$accentColor};
    box-shadow: 0 20px 60px ${props => props.$shadowColor};

    .card-arrow {
      transform: translateX(8px);
    }

    .card-icon {
      animation: ${pulse} 0.6s ease;
    }
  }

  @media (max-width: 600px) {
    min-height: 380px;
    padding: 30px;
  }
`;

const CardBgImage = styled.div`
  position: absolute;
  inset: 0;
  background: url(${props => props.$image}) center/cover no-repeat;
  z-index: 0;
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
`;

const CardIcon = styled.div`
  width: 70px;
  height: 70px;
  background: ${props => props.$gradient};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 10px 30px ${props => props.$shadow};

  svg {
    font-size: 2rem;
    color: ${colors.text.white};
  }
`;

const CardLabel = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${props => props.$color};
  margin-bottom: 10px;
`;

const CardTitle = styled.h2`
  font-size: 2.2rem;
  color: ${colors.text.heading};
  margin-bottom: 15px;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 1.8rem;
  }
`;

const CardDescription = styled.p`
  font-size: 1rem;
  color: ${colors.text.light};
  line-height: 1.7;
  margin-bottom: 25px;
  max-width: 400px;
`;

const CardCTA = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${props => props.$color};

  svg {
    transition: transform 0.3s ease;
  }
`;

const ShineOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, ${props => props.$color}, transparent);
  background-size: 200% 100%;
  animation: ${shine} 3s linear infinite;
  z-index: 3;
`;

// QUICK SERVICES PREVIEW
const ServicesPreview = styled.section`
  padding: 100px 20px;
  background: ${colors.background.medium};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionLabel = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${colors.accent.main};
  margin-bottom: 15px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3rem);
  color: ${colors.text.heading};
  margin-bottom: 15px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  max-width: 600px;
  margin: 0 auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto 50px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceMiniCard = styled.div`
  background: ${colors.background.card};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.accent.main};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }

  svg {
    font-size: 2rem;
    color: ${colors.accent.main};
    margin-bottom: 15px;
  }

  h4 {
    font-family: 'Oswald', sans-serif;
    font-size: 1rem;
    text-transform: uppercase;
    color: ${colors.text.heading};
    letter-spacing: 1px;
  }
`;

const ViewAllBtn = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: fit-content;
  margin: 0 auto;
  padding: 16px 40px;
  background: transparent;
  border: 2px solid ${colors.accent.main};
  color: ${colors.accent.light};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.accent.main};
    color: ${colors.text.white};

    svg {
      transform: translateX(5px);
    }
  }

  svg {
    transition: transform 0.3s ease;
  }
`;

// ABOUT / WHY US SECTION
const AboutSection = styled.section`
  padding: 100px 20px;
  background: ${colors.background.card};
  position: relative;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const AboutContent = styled.div``;

const AboutTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;

  span {
    color: ${colors.accent.main};
  }

  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${colors.metallic.silver};
    margin-top: 16px;
    border-radius: 2px;
  }
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 30px;
`;

const AboutBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  background: transparent;
  border: 2px solid ${colors.accent.main};
  color: ${colors.accent.light};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.accent.main};
    color: ${colors.text.white};
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

const WhyUsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const WhyUsCard = styled.div`
  background: ${colors.background.medium};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 25px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.accent.main};
    transform: translateY(-3px);
  }
`;

const WhyUsIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${colors.metallic.darkSteel};
  border: 1px solid ${colors.borderLight};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;

  svg {
    font-size: 1.4rem;
    color: ${colors.accent.main};
  }
`;

const WhyUsTitle = styled.h4`
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.text.heading};
  margin-bottom: 8px;
`;

const WhyUsText = styled.p`
  font-size: 0.9rem;
  color: ${colors.text.light};
  line-height: 1.6;
`;

// GOOGLE REVIEWS SECTION
const ReviewsSection = styled.section`
  padding: 100px 20px;
  background: ${colors.background.dark};
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ReviewCard = styled.div`
  background: ${colors.background.card};
  border: 1px solid ${colors.borderLight};
  border-radius: 12px;
  padding: 35px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: ${colors.accent.main};
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;

  svg {
    color: ${colors.accent.main};
    font-size: 1.2rem;
  }
`;

const ReviewQuote = styled.p`
  font-size: 1rem;
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 20px;
  font-style: italic;
`;

const ReviewAuthor = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  color: ${colors.text.heading};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ReviewSource = styled.div`
  font-size: 0.85rem;
  color: ${colors.text.muted};
  margin-top: 4px;
`;

const miniServices = [
  { icon: <FiTool />, name: 'Oil Changes' },
  { icon: <FiShield />, name: 'Brake Service' },
  { icon: <FiClock />, name: 'Diagnostics' },
  { icon: <FiDollarSign />, name: 'Fair Pricing' },
];

const testimonials = [
  {
    quote: "Needed tires changed but my regular place was booked for weeks. Couldn't find anywhere until a friend mentioned Dave. I've been bringing my car here ever since. He does good work and I love supporting local businesses.",
    name: "Nin Kasi",
  },
  {
    quote: "David does a great job! He is fast, competent and less expensive than most. Also offers great customer service!! Was more than pleased with his work.",
    name: "Bonnie Daigneault",
  },
  {
    quote: "Nice Guy, and helped me out with an unexpected quick fix. Changed over my tires to snows real efficiently too. Would recommend.",
    name: "Anthony Pellegrino",
  },
];

export default function HomePage() {
  usePageMeta(
    'The Honest Mechanic | BMW Specialist & Auto Repair in Charlotte, VT',
    'Honest auto repair in Charlotte, VT. BMW specialist with 8+ years experience. Oil changes, brakes, engine swaps, diagnostics & more. No surprises, no upsells — just fair prices.'
  );

  return (
    <>
      {/* HERO */}
      <HeroSection>
        <Container>
          <HeroContent>
            <Badge>
              <FiShield /> BMW Specialist
            </Badge>
            <Title>
              Honest Work.<br />
              <span>Fair Prices.</span>
            </Title>
            <Subtitle>
              No surprises, no upsells. Just quality auto repair from
              folks who treat your car like their own. We're your neighbors
              in Charlotte, VT — the only shop in town — and we're here to help.
            </Subtitle>
            <Buttons>
              <PrimaryBtn to="/contact">
                Schedule Service <FiArrowRight />
              </PrimaryBtn>
              <SecondaryBtn to="/services">
                View Services
              </SecondaryBtn>
            </Buttons>
            <Features>
              <Feature>
                <FiShield /> Experienced Mechanics
              </Feature>
              <Feature>
                <FiClock /> Quick Turnaround
              </Feature>
              <Feature>
                <FiDollarSign /> Fair Pricing
              </Feature>
            </Features>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* SPOTLIGHT - BMW & ACADEMY */}
      <SpotlightSection>
        <Container>
          <SectionHeader>
            <SectionLabel>What Sets Us Apart</SectionLabel>
            <SectionTitle>More Than Your Average Shop</SectionTitle>
          </SectionHeader>
          <SpotlightGrid>
            {/* BMW SPECIALTY CARD */}
            <SpotlightCard
              to="/bmw"
              $gradient="linear-gradient(135deg, rgba(0, 102, 177, 0.3) 0%, rgba(0, 71, 133, 0.5) 100%)"
              $accentColor={colors.accent.main}
              $shadowColor="rgba(0, 102, 177, 0.3)"
            >
              <CardBgImage $image="/images/bmw-show.jpg" />
              <ShineOverlay $color={colors.accent.light} />
              <CardContent>
                <CardIcon className="card-icon" $gradient={colors.accent.gradient} $shadow="rgba(0, 102, 177, 0.4)">
                  <FiAward />
                </CardIcon>
                <CardLabel $color={colors.accent.light}>Specialty</CardLabel>
                <CardTitle>BMW Experts</CardTitle>
                <CardDescription>
                  8+ years of hands-on BMW experience. We understand the precision engineering
                  and give your ultimate driving machine the specialized care it deserves.
                </CardDescription>
                <CardCTA className="card-arrow" $color={colors.accent.light}>
                  Learn More <FiArrowRight />
                </CardCTA>
              </CardContent>
            </SpotlightCard>

            {/* ACADEMY CARD */}
            <SpotlightCard
              to="/academy"
              $gradient="linear-gradient(135deg, rgba(0, 102, 177, 0.3) 0%, rgba(0, 71, 133, 0.5) 100%)"
              $accentColor={colors.accent.main}
              $shadowColor="rgba(0, 102, 177, 0.3)"
            >
              <CardBgImage $image="/images/brake-work.jpg" />
              <ShineOverlay $color={colors.accent.light} />
              <CardContent>
                <CardIcon className="card-icon" $gradient={colors.accent.gradient} $shadow="rgba(0, 102, 177, 0.4)">
                  <FiUsers />
                </CardIcon>
                <CardLabel $color={colors.accent.light}>Community Education</CardLabel>
                <CardTitle>HM Academy</CardTitle>
                <CardDescription>
                  Learn basic car maintenance, safety skills, and when to DIY vs. call a pro.
                  Classes for youth (11-14) and adults. Empower yourself with knowledge.
                </CardDescription>
                <CardCTA className="card-arrow" $color={colors.accent.light}>
                  View Classes <FiArrowRight />
                </CardCTA>
              </CardContent>
            </SpotlightCard>
          </SpotlightGrid>
        </Container>
      </SpotlightSection>

      {/* QUICK SERVICES */}
      <ServicesPreview>
        <Container>
          <SectionHeader>
            <SectionLabel>What We Do</SectionLabel>
            <SectionTitle>Quality Service, Every Time</SectionTitle>
            <SectionSubtitle>
              From routine maintenance to complex repairs, we've got you covered.
            </SectionSubtitle>
          </SectionHeader>
          <ServicesGrid>
            {miniServices.map((service, i) => (
              <ServiceMiniCard key={i}>
                {service.icon}
                <h4>{service.name}</h4>
              </ServiceMiniCard>
            ))}
          </ServicesGrid>
          <ViewAllBtn to="/services">
            View All Services <FiArrowRight />
          </ViewAllBtn>
        </Container>
      </ServicesPreview>

      {/* ABOUT / WHY US */}
      <AboutSection>
        <AboutGrid>
          <AboutContent>
            <SectionLabel>Why Choose Us</SectionLabel>
            <AboutTitle>
              Your Neighbors in <span>Charlotte, VT</span>
            </AboutTitle>
            <AboutText>
              We're the only shop in town, and we take that responsibility seriously.
              When you bring your car to us, you're not just another ticket — you're a neighbor.
              We'll explain what's wrong in plain English, give you honest recommendations,
              and never push services you don't need.
            </AboutText>
            <AboutBtn to="/about">
              Learn Our Story <FiArrowRight />
            </AboutBtn>
          </AboutContent>

          <WhyUsGrid>
            <WhyUsCard>
              <WhyUsIcon><FiCheck /></WhyUsIcon>
              <WhyUsTitle>No Surprises</WhyUsTitle>
              <WhyUsText>Upfront estimates before any work begins. We call first if anything changes.</WhyUsText>
            </WhyUsCard>
            <WhyUsCard>
              <WhyUsIcon><FiHeart /></WhyUsIcon>
              <WhyUsTitle>Honest Advice</WhyUsTitle>
              <WhyUsText>We only recommend repairs you actually need. No upsells, no pressure.</WhyUsText>
            </WhyUsCard>
            <WhyUsCard>
              <WhyUsIcon><FiTarget /></WhyUsIcon>
              <WhyUsTitle>Quality Work</WhyUsTitle>
              <WhyUsText>We stand behind every repair. Your car gets treated like it's our own.</WhyUsText>
            </WhyUsCard>
            <WhyUsCard>
              <WhyUsIcon><FiShield /></WhyUsIcon>
              <WhyUsTitle>BMW Expertise</WhyUsTitle>
              <WhyUsText>8+ years specializing in BMW. We know these machines inside and out.</WhyUsText>
            </WhyUsCard>
          </WhyUsGrid>
        </AboutGrid>
      </AboutSection>

      {/* GOOGLE REVIEWS */}
      <ReviewsSection>
        <Container>
          <SectionHeader>
            <SectionLabel>What People Say</SectionLabel>
            <SectionTitle>Google Reviews</SectionTitle>
            <SectionSubtitle>
              Real feedback from real neighbors. See why folks trust us with their vehicles.
            </SectionSubtitle>
          </SectionHeader>
          <ReviewsGrid>
            {testimonials.map((t, i) => (
              <ReviewCard key={i}>
                <Stars>
                  {[...Array(5)].map((_, j) => <FiStar key={j} fill={colors.accent.main} />)}
                </Stars>
                <ReviewQuote>"{t.quote}"</ReviewQuote>
                <ReviewAuthor>{t.name}</ReviewAuthor>
                <ReviewSource>Google Review</ReviewSource>
              </ReviewCard>
            ))}
          </ReviewsGrid>
        </Container>
      </ReviewsSection>
    </>
  );
}
